import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check, KeyRound, Loader2, LogOut, Plus, Receipt, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { sumIsk, fmtIsk, fmtNum2, fmtUsdApprox, ISK_PER_USD, moneyToIsk, nameKey, hotelPriceForTotal } from "@/lib/price";
import { DualPrice } from "@/components/DualPrice";

const inputCls =
  "border border-amber-900/40 bg-[#0d0d12] px-3 py-2.5 text-sm text-amber-50 placeholder:text-neutral-600 outline-none transition-colors focus:border-amber-400";

const fmtD = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

export default function OwnerDebts() {
  const navigate = useNavigate();
  const token = localStorage.getItem("er-owner-token") || "";
  const headers = { Authorization: `Bearer ${token}` };

  const [debts, setDebts] = useState(null);
  const [trips, setTrips] = useState([]);
  const [client, setClient] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [vatReport, setVatReport] = useState(null);

  const load = () => {
    axios.get(`${API}/owner/debts`, { headers }).then((res) => setDebts(res.data.debts)).catch(() => setDebts([]));
    axios.get(`${API}/owner/trips`, { headers }).then((res) => setTrips(res.data.trips || [])).catch(() => {});
    axios.get(`${API}/owner/vat-report`, { headers }).then((res) => setVatReport(res.data)).catch(() => {});
    axios
      .get(`${API}/owner/invoices`, { headers })
      .then((res) => setOpenInvoices((res.data.invoices || []).filter((i) => i.status !== "paid" && i.totals.balance_isk > 0)))
      .catch(() => setOpenInvoices([]));
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const clientNames = useMemo(
    () => [...new Set(trips.map((t) => t.client?.name).filter(Boolean))],
    [trips]
  );

  const unpaid = (debts || []).filter((d) => !d.paid);
  const paid = (debts || []).filter((d) => d.paid);
  const outstandingIsk = useMemo(() => sumIsk(unpaid.map((d) => d.amount)), [debts]);

  // whole-business sums across every journey — same mirror-aware math as the trip cards:
  // stay-linked itinerary lines are skipped (the stay owns the money), rentals count once
  const bizTotals = useMemo(() => {
    let bill = 0, cost = 0, profit = 0;
    const isMirrored = (t, i) => {
      if (i.type !== "hotel") return false;
      const tt = nameKey(i.title);
      return (t.hotels || []).some((h) => {
        const hn = nameKey(h.name);
        if (!hn) return false;
        const first = hn.split(/\s+/)[0];
        return tt.includes(hn) || (first.length > 3 && tt.includes(first));
      });
    };
    (trips || []).forEach((t) => {
      const rows = [
        ...(t.hotels || []).map((h) => ({ ...h, price: hotelPriceForTotal(h) })),
        ...(t.items || []).filter((i) => !isMirrored(t, i)),
        ...(t.rentals || []),
      ];
      rows.forEach((r) => {
        const p = moneyToIsk(r.price) || 0;
        const c = moneyToIsk(r.cost) || 0;
        const cm = moneyToIsk(r.commission) || 0;
        bill += p;
        cost += c;
        profit += cm || (p && c ? p - c : 0);
      });
    });
    return { bill, cost, profit };
  }, [trips]);

  // VAT across all filing periods: charged on sales minus credit from vendor invoices
  const vatTotals = useMemo(
    () => ({
      charged: vatReport?.charged_isk || 0,
      credit: vatReport?.credit_isk || 0,
      due: vatReport?.difference_isk || 0,
    }),
    [vatReport]
  );

  if (!token) {
    return (
      <div data-testid="debts-locked" className="flex min-h-screen flex-col items-center justify-center bg-[#070709] px-5 text-center">
        <img src="/assets/logo.png" alt="Exclusive Reykjavik" className="h-16 w-auto mix-blend-screen" />
        <p className="mt-8 font-serif text-3xl font-light text-neutral-200">The private door is closed.</p>
        <p className="mt-3 max-w-sm text-sm text-neutral-500">Sign in at the concierge desk first — your key opens the debt list too.</p>
        <Link
          to="/owner"
          data-testid="debts-goto-login"
          className="mt-8 flex items-center gap-2 bg-amber-500 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300"
        >
          <KeyRound className="h-4 w-4" /> Go to Sign-In
        </Link>
      </div>
    );
  }

  const add = async () => {
    if (!client.trim() || adding) return;
    setAdding(true);
    try {
      await axios.post(`${API}/owner/debts`, { client, amount, note }, { headers });
      setClient("");
      setAmount("");
      setNote("");
      toast.success("Debt added");
      load();
    } catch {
      toast.error("Could not add the debt.");
    } finally {
      setAdding(false);
    }
  };

  const mark = async (id, paidFlag) => {
    try {
      await axios.patch(`${API}/owner/debts/${id}`, { paid: paidFlag }, { headers });
      load();
    } catch {
      toast.error("Could not update the debt.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/owner/debts/${id}`, { headers });
      toast.success("Debt removed");
      load();
    } catch {
      toast.error("Could not remove the debt.");
    }
  };

  return (
    <div data-testid="debts-page" className="min-h-screen bg-[#070709] text-neutral-100">
      <header className="sticky top-0 z-40 border-b border-amber-500/15 bg-[#0c0c10]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="flex items-center gap-4">
            <img src="/assets/logo.png" alt="" className="h-10 w-auto mix-blend-screen" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Concierge Desk</p>
              <h1 className="font-serif text-xl font-light text-neutral-50">Debt List</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/owner/trips"
              data-testid="debts-goto-trips"
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              Trip Builder
            </Link>
            <Link
              to="/owner"
              data-testid="debts-goto-requests"
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Requests
            </Link>
            <button
              data-testid="debts-logout-button"
              onClick={() => {
                localStorage.removeItem("er-owner-token");
                navigate("/owner");
              }}
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-red-400 hover:text-red-300"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-10">
        <div className="mb-6 grid gap-3 sm:grid-cols-3" data-testid="debts-biz-totals">
          <div className="border border-amber-500/20 bg-[#0c0c10] p-4">
            <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Total cost — all journeys</p>
            <p data-testid="debts-total-cost" className="mt-1 font-luxe text-xl tracking-[0.1em] text-amber-100">{fmtIsk(bizTotals.cost)}</p>
          </div>
          <div className="border border-amber-500/20 bg-[#0c0c10] p-4">
            <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Total profit — all journeys</p>
            <p data-testid="debts-total-profit" className="mt-1 font-luxe text-xl tracking-[0.1em] text-emerald-300">{fmtIsk(bizTotals.profit)}</p>
          </div>
          <div className="border border-amber-500/20 bg-[#0c0c10] p-4">
            <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">VAT to pay — all periods</p>
            <p data-testid="debts-total-vat" className="mt-1 font-luxe text-xl tracking-[0.1em] text-amber-100">{fmtIsk(vatTotals.due)}</p>
            <p className="mt-0.5 text-[9px] uppercase tracking-[0.15em] text-neutral-600">
              charged {fmtIsk(vatTotals.charged)} − credit {fmtIsk(vatTotals.credit)}
            </p>
          </div>
        </div>

        <div className="border border-amber-500/20 bg-[#0c0c10] p-5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Add an unpaid debt</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_160px_1fr_auto]">
            <input
              data-testid="debt-client-input"
              list="debt-client-names"
              placeholder="Client name *"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              className={inputCls}
            />
            <datalist id="debt-client-names">
              {clientNames.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
            <input
              data-testid="debt-amount-input"
              placeholder="Amount, e.g. 150.000 ISK"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              className={inputCls}
            />
            <input
              data-testid="debt-note-input"
              placeholder="What is it for?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              className={inputCls}
            />
            <button
              data-testid="debt-add-button"
              onClick={add}
              disabled={adding || !client.trim()}
              className="flex items-center justify-center gap-2 bg-amber-500 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-300 disabled:opacity-50"
            >
              {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
            </button>
          </div>
        </div>

        <div className="mt-8 flex items-baseline justify-between border-b border-amber-500/20 pb-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">
            Outstanding — {unpaid.length} {unpaid.length === 1 ? "debt" : "debts"}
          </p>
          {outstandingIsk > 0 && (
            <p data-testid="debts-outstanding" className="font-luxe text-xl tracking-wide text-amber-100">
              {fmtIsk(outstandingIsk)}{" "}
              <span className="text-sm font-normal normal-case tracking-normal text-neutral-500">{fmtUsdApprox(outstandingIsk / ISK_PER_USD)}</span>
            </p>
          )}
        </div>

        {openInvoices.length > 0 && (
          <div data-testid="debts-open-invoices" className="mt-6 border border-amber-500/20 bg-[#0c0c10] p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">
                <Receipt className="h-3.5 w-3.5" /> Open invoices — {openInvoices.length}
              </p>
              <p data-testid="debts-invoices-total" className="font-luxe text-lg tracking-wide text-amber-100">
                <DualPrice value={`${fmtNum2(openInvoices.reduce((a, i) => a + i.totals.balance_isk, 0))} ISK`} />
              </p>
            </div>
            <div className="mt-3 divide-y divide-neutral-800/70">
              {openInvoices.map((inv) => (
                <div key={inv.id} data-testid={`debts-invoice-${inv.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                  <p className="text-sm text-neutral-200">
                    <span className="mr-2 text-xs tracking-[0.15em] text-amber-300/90">{inv.invoice_number}</span>
                    {inv.client_name}
                    {inv.due_date && <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-neutral-500">due {fmtD(inv.due_date)}</span>}
                  </p>
                  <span className="font-luxe tracking-wide text-amber-100">
                    <DualPrice value={`${fmtNum2(inv.totals.balance_isk)} ISK`} />
                  </span>
                </div>
              ))}
            </div>
            <Link
              to="/owner/invoices"
              data-testid="debts-goto-invoices"
              className="mt-3 inline-block border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
            >
              Open invoices →
            </Link>
          </div>
        )}

        <div className="mt-2 divide-y divide-neutral-800/70">
          {debts === null ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
            </div>
          ) : unpaid.length === 0 ? (
            <p data-testid="debts-empty" className="py-10 text-center font-serif text-lg italic text-neutral-500">
              Nothing outstanding — every client is squared away.
            </p>
          ) : (
            unpaid.map((d) => (
              <div key={d.id} data-testid={`debt-row-${d.id}`} className="flex flex-wrap items-center justify-between gap-3 py-3.5">
                <div className="min-w-0">
                  <p className="font-serif text-lg leading-tight text-neutral-50">{d.client}</p>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                    {d.note || "—"} · since {fmtD(d.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {d.amount && (
                    <span data-testid={`debt-amount-${d.id}`} className="font-luxe text-lg tracking-wide text-amber-100">
                      <DualPrice value={d.amount} />
                    </span>
                  )}
                  <button
                    data-testid={`debt-paid-${d.id}`}
                    onClick={() => mark(d.id, true)}
                    className="flex items-center gap-1.5 border border-emerald-500/50 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-emerald-200 transition-colors hover:bg-emerald-500 hover:text-black"
                  >
                    <Check className="h-3 w-3" /> Paid
                  </button>
                  <button
                    data-testid={`debt-delete-${d.id}`}
                    onClick={() => remove(d.id)}
                    className="text-neutral-600 transition-colors hover:text-red-400"
                    aria-label="Delete debt"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {paid.length > 0 && (
          <div className="mt-10">
            <p className="border-b border-neutral-800 pb-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-600">
              Settled — {paid.length}
            </p>
            <div className="divide-y divide-neutral-800/50">
              {paid.map((d) => (
                <div key={d.id} data-testid={`debt-settled-${d.id}`} className="flex flex-wrap items-center justify-between gap-3 py-3 opacity-50">
                  <div className="min-w-0">
                    <p className="font-serif text-base leading-tight text-neutral-300 line-through">{d.client}</p>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-600">
                      {d.note || "—"} · paid {fmtD(d.paid_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {d.amount && <DualPrice value={d.amount} className="font-luxe text-base tracking-wide text-neutral-400" />}
                    <button
                      data-testid={`debt-undo-${d.id}`}
                      onClick={() => mark(d.id, false)}
                      className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-400 transition-colors hover:border-amber-400 hover:text-amber-200"
                    >
                      <Undo2 className="h-3 w-3" /> Undo
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
